<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('indietro') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-language me-2 text-primary"></i>
            {{ $t('traduzione_menu') || 'Studio Traduzioni Menu con AI' }}
          </h1>
          <p class="sci-subtitle">Traduci piatti e prodotti per i menu digitali multilingua (IT, EN, ES)</p>
        </div>
      </div>
      <div class="sci-header-right">
        <span class="sci-badge sci-badge-stats">
          <i class="fas fa-globe me-1"></i>
          {{ translatedEnCount }} EN / {{ translatedEsCount }} ES tradotti su {{ items.length }} totali
        </span>
      </div>
    </header>

    <!-- Main Bento Spatial Grid -->
    <main class="sci-bento-grid">
      <!-- Left Column: Translation Studio Control -->
      <aside class="sci-card control-panel">
        <div class="sci-card-header">
          <h2 class="sci-card-title">
            <i class="fas fa-sliders-h me-2 text-primary"></i>
            Impostazioni Traduzione
          </h2>
          <span class="sci-caption">Configura lingue target e filtri tipologia</span>
        </div>

        <div class="control-body">
          <!-- Item Type Filter -->
          <div class="sci-form-group">
            <label class="sci-label">Filtra Tipologia:</label>
            <div class="type-filter-group">
              <button 
                class="type-pill" 
                :class="{ active: typeFilter === 'ALL' }"
                @click="typeFilter = 'ALL'"
              >
                Tutti ({{ items.length }})
              </button>
              <button 
                class="type-pill" 
                :class="{ active: typeFilter === 'Piatto' }"
                @click="typeFilter = 'Piatto'"
              >
                Piatti ({{ piattiCount }})
              </button>
              <button 
                class="type-pill" 
                :class="{ active: typeFilter === 'Prodotto' }"
                @click="typeFilter = 'Prodotto'"
              >
                Prodotti ({{ prodottiCount }})
              </button>
            </div>
          </div>

          <!-- Target Languages Selector -->
          <div class="sci-form-group">
            <label class="sci-label">Lingue di Destinazione:</label>
            <div class="lang-chips-grid">
              <label class="lang-chip" :class="{ active: selectedLangs.includes('en') }">
                <input type="checkbox" value="en" v-model="selectedLangs" class="visually-hidden">
                <span class="flag-icon">🇬🇧</span>
                <span class="lang-name">Inglese (EN)</span>
              </label>
              <label class="lang-chip" :class="{ active: selectedLangs.includes('es') }">
                <input type="checkbox" value="es" v-model="selectedLangs" class="visually-hidden">
                <span class="flag-icon">🇪🇸</span>
                <span class="lang-name">Spagnolo (ES)</span>
              </label>
              <label class="lang-chip" :class="{ active: selectedLangs.includes('it') }">
                <input type="checkbox" value="it" v-model="selectedLangs" class="visually-hidden">
                <span class="flag-icon">🇮🇹</span>
                <span class="lang-name">Italiano (IT)</span>
              </label>
            </div>
          </div>

          <!-- Selection Summary & Actions -->
          <div class="selection-card">
            <div class="selection-card-header">
              <span class="selection-count-badge">
                {{ selectedItems.length }} di {{ filteredItems.length }} selezionati
              </span>
              <div class="selection-quick-btns">
                <button class="btn-subtle" @click="selectAll">{{ $t('seleziona_tutti') || 'Tutti' }}</button>
                <span class="divider">|</span>
                <button class="btn-subtle" @click="deselectAll">{{ $t('deseleziona_tutti') || 'Nessuno' }}</button>
              </div>
            </div>

            <button 
              class="sci-btn sci-btn-primary w-100 btn-translate-cta"
              :disabled="isTranslating || selectedItems.length === 0 || selectedLangs.length === 0"
              @click="startTranslation"
            >
              <i class="fas" :class="isTranslating ? 'fa-spinner fa-spin' : 'fa-magic'"></i>
              <span>
                {{ isTranslating ? 'Traduzione AI in corso...' : 'Traduci Selezionati (' + selectedItems.length + ')' }}
              </span>
            </button>
          </div>

          <!-- AI Context Helper -->
          <div class="ai-info-box">
            <div class="info-header">
              <i class="fas fa-sparkles text-primary"></i>
              <span class="info-title">Traduzione Gastronomica</span>
            </div>
            <p class="info-text">
              Il motore AI applica modelli linguistici ottimizzati per il settore Ho.Re.Ca., preservando le denominazioni tipiche italiane e traducendo accuratamente descrizioni ed ingredienti.
            </p>
          </div>
        </div>
      </aside>

      <!-- Right Column: Multilingual Catalog Table -->
      <section class="sci-card catalog-panel">
        <div class="sci-card-header catalog-header">
          <div class="catalog-search-wrap">
            <i class="fas fa-search search-icon"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Cerca per nome originale, tradotto o descrizione..." 
              class="sci-search-input"
            />
            <button 
              v-if="searchQuery" 
              class="clear-search-btn" 
              @click="searchQuery = ''"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div class="table-responsive">
          <table class="sci-table">
            <thead>
              <tr>
                <th style="width: 48px; text-align: center;">
                  <input 
                    type="checkbox" 
                    @change="toggleAll($event)" 
                    :checked="isAllSelected"
                    class="sci-checkbox"
                  />
                </th>
                <th style="width: 100px;">Tipo</th>
                <th>Nome Originale (IT)</th>
                <th>Traduzione Inglese (EN)</th>
                <th>Traduzione Spagnolo (ES)</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="item in filteredItems" 
                :key="item.id" 
                class="sci-table-row"
                :class="{ 'row-selected': selectedItems.includes(item.id) }"
                @click="toggleSelection(item.id)"
              >
                <!-- Checkbox -->
                <td style="text-align: center;" @click.stop>
                  <input 
                    type="checkbox" 
                    :value="item.id" 
                    v-model="selectedItems"
                    class="sci-checkbox"
                  />
                </td>

                <!-- Type Badge -->
                <td>
                  <span class="badge-type" :class="item.type === 'Piatto' ? 'badge-type-dish' : 'badge-type-product'">
                    {{ item.type }}
                  </span>
                </td>

                <!-- Original Italian -->
                <td>
                  <div class="dish-item-cell">
                    <span class="item-name-it">{{ item.nome }}</span>
                    <span v-if="item.portata" class="item-portata-tag">{{ item.portata }}</span>
                    <p v-if="item.descrizione" class="item-desc-text">{{ item.descrizione }}</p>
                  </div>
                </td>

                <!-- English Translation -->
                <td>
                  <div v-if="item.nomeEn" class="trans-item">
                    <span class="badge-lang-ok">EN</span>
                    <span class="trans-text">{{ item.nomeEn }}</span>
                  </div>
                  <div v-else class="trans-missing">
                    <span class="badge-lang-missing">EN</span>
                    <span class="trans-missing-text">Mancante</span>
                  </div>
                </td>

                <!-- Spanish Translation -->
                <td>
                  <div v-if="item.nomeEs" class="trans-item">
                    <span class="badge-lang-ok">ES</span>
                    <span class="trans-text">{{ item.nomeEs }}</span>
                  </div>
                  <div v-else class="trans-missing">
                    <span class="badge-lang-missing">ES</span>
                    <span class="trans-missing-text">Mancante</span>
                  </div>
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-if="filteredItems.length === 0">
                <td colspan="5" class="sci-empty-state">
                  <div class="empty-content">
                    <div class="empty-icon-halo">
                      <i class="fas fa-language"></i>
                    </div>
                    <h3 class="empty-title">Nessun elemento trovato</h3>
                    <p class="empty-desc">
                      {{ searchQuery ? 'Nessun piatto o prodotto corrisponde ai criteri di ricerca.' : 'Nessun elemento presente nel catalogo.' }}
                    </p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'Traduci',
  data() {
    return {
      items: [],
      selectedItems: [],
      selectedLangs: ['en', 'es'],
      searchQuery: '',
      typeFilter: 'ALL',
      isTranslating: false,
      store
    }
  },
  computed: {
    piattiCount() {
      return this.items.filter(i => i.type === 'Piatto').length;
    },
    prodottiCount() {
      return this.items.filter(i => i.type === 'Prodotto').length;
    },
    translatedEnCount() {
      return this.items.filter(i => !!i.nomeEn).length;
    },
    translatedEsCount() {
      return this.items.filter(i => !!i.nomeEs).length;
    },
    filteredItems() {
      let list = this.items;
      if (this.typeFilter !== 'ALL') {
        list = list.filter(i => i.type === this.typeFilter);
      }
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter(i => 
          (i.nome && i.nome.toLowerCase().includes(q)) || 
          (i.nomeEn && i.nomeEn.toLowerCase().includes(q)) || 
          (i.nomeEs && i.nomeEs.toLowerCase().includes(q)) || 
          (i.descrizione && i.descrizione.toLowerCase().includes(q)) ||
          (i.portata && i.portata.toLowerCase().includes(q))
        );
      }
      return list;
    },
    isAllSelected() {
      return this.filteredItems.length > 0 && this.selectedItems.length >= this.filteredItems.length;
    }
  },
  methods: {
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    async fetchData() {
      try {
        const config = {
          headers: { authorization: `Bearer ${JSON.parse(localStorage.token)}` }
        };

        // Fetch Menu (Piatti)
        const menuRes = await this.axios.post(this.store.path + 'displayMenu', config);
        const piatti = (menuRes.data.messaggio || []).map(p => ({
          id: p.id || p.nome,
          nome: p.nome,
          nomeEn: p.nomeEn,
          nomeEs: p.nomeEs,
          descrizione: p.descrizione,
          portata: p.portata,
          type: 'Piatto'
        }));

        // Fetch Products (Mappature)
        const prodRes = await this.axios.post(this.store.path + 'displayProduct', config);
        const prodotti = (prodRes.data.messaggio || []).map(p => ({
          id: p.code || p.nome,
          nome: p.nome || p.descrizio,
          nomeEn: p.nomeEn,
          nomeEs: p.nomeEs,
          descrizione: p.descrizio,
          type: 'Prodotto'
        }));

        this.items = [...piatti, ...prodotti];
      } catch (error) {
        console.error("Errore caricamento dati per traduzione:", error);
      }
    },
    toggleAll(e) {
      if (e.target.checked) {
        this.selectedItems = this.filteredItems.map(i => i.id);
      } else {
        this.selectedItems = [];
      }
    },
    selectAll() {
      this.selectedItems = this.filteredItems.map(i => i.id);
    },
    deselectAll() {
      this.selectedItems = [];
    },
    toggleSelection(id) {
      const index = this.selectedItems.indexOf(id);
      if (index === -1) {
        this.selectedItems.push(id);
      } else {
        this.selectedItems.splice(index, 1);
      }
    },
    async startTranslation() {
      if (this.selectedItems.length === 0) return;
      
      this.isTranslating = true;
      try {
        const itemsToTranslate = this.items.filter(i => this.selectedItems.includes(i.id));
        
        const response = await this.axios.post(this.store.path + 'translate', {
          items: itemsToTranslate,
          languages: this.selectedLangs
        }, {
          headers: { authorization: `Bearer ${JSON.parse(localStorage.token)}` }
        });

        alert(response.data.messaggio || 'Traduzioni completate con successo!');
        await this.fetchData();
        this.selectedItems = [];
      } catch (error) {
        console.error("Errore traduzione:", error);
        alert("Si è verificato un errore durante la traduzione.");
      } finally {
        this.isTranslating = false;
      }
    }
  },
  mounted() {
    this.fetchData();
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 50px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.04);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-title-group {
  display: flex;
  flex-direction: column;
}

.sci-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.sci-badge-stats {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

/* Bento Grid */
.sci-bento-grid {
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 24px;
  max-width: 1400px;
  margin: 24px auto;
  padding: 0 24px;
  width: 100%;
}

@media (max-width: 960px) {
  .sci-bento-grid {
    grid-template-columns: 1fr;
  }
}

/* Bento Cards */
.sci-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}

.sci-card-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
}

.sci-card-title {
  margin: 0 0 4px 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
}

.sci-caption {
  font-size: 0.85rem;
  color: #64748b;
}

/* Left Panel */
.control-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.sci-form-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sci-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
}

.type-filter-group {
  display: flex;
  gap: 8px;
}

.type-pill {
  flex: 1;
  min-height: 40px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
}

.type-pill:hover {
  background: #f1f5f9;
}

.type-pill.active {
  background: #0f172a;
  color: #ffffff;
  border-color: #0f172a;
}

.lang-chips-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.lang-chip {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 10px 16px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.lang-chip:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.lang-chip.active {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #1d4ed8;
  font-weight: 600;
}

.flag-icon {
  font-size: 1.25rem;
}

.lang-name {
  font-size: 0.95rem;
}

/* Selection Summary Card */
.selection-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.selection-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selection-count-badge {
  font-size: 0.88rem;
  font-weight: 600;
  color: #334155;
}

.selection-quick-btns {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-subtle {
  background: none;
  border: none;
  color: #2563eb;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.btn-subtle:hover {
  text-decoration: underline;
}

.divider {
  color: #cbd5e1;
  font-size: 0.85rem;
}

.btn-translate-cta {
  min-height: 48px;
  font-size: 1rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.35);
}

.btn-translate-cta:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-1px);
}

.btn-translate-cta:disabled {
  background: #cbd5e1;
  color: #94a3b8;
  box-shadow: none;
  cursor: not-allowed;
}

/* AI Info Box */
.ai-info-box {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 16px;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.info-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #166534;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.info-text {
  margin: 0;
  font-size: 0.85rem;
  color: #15803d;
  line-height: 1.45;
}

/* Right Panel: Catalog Table */
.catalog-header {
  padding: 16px 24px;
}

.catalog-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 14px;
  color: #94a3b8;
  font-size: 0.95rem;
}

.sci-search-input {
  width: 100%;
  min-height: 44px;
  padding: 8px 40px 8px 40px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #0f172a;
}

.sci-search-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
}

.clear-search-btn {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 6px;
}

.clear-search-btn:hover {
  color: #ef4444;
}

/* Table */
.sci-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  text-align: left;
}

.sci-table thead th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 14px 20px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.sci-table-row {
  cursor: pointer;
}

.sci-table-row td {
  padding: 14px 20px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  transition: background 0.15s ease;
}

.sci-table-row:hover td {
  background: #f8fafc;
}

.sci-table-row.row-selected td {
  background: #eff6ff;
}

.sci-checkbox {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  accent-color: #10b981;
  cursor: pointer;
}

.badge-type {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
}

.badge-type-dish {
  background: #e0f2fe;
  color: #0369a1;
}

.badge-type-product {
  background: #f1f5f9;
  color: #475569;
}

.dish-item-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.item-name-it {
  font-size: 0.98rem;
  font-weight: 700;
  color: #0f172a;
}

.item-portata-tag {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 600;
}

.item-desc-text {
  margin: 4px 0 0 0;
  font-size: 0.82rem;
  color: #64748b;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Translations badges */
.trans-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.badge-lang-ok {
  background: #dcfce7;
  color: #15803d;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
}

.trans-text {
  font-size: 0.92rem;
  font-weight: 600;
  color: #1e293b;
}

.trans-missing {
  display: flex;
  align-items: center;
  gap: 8px;
}

.badge-lang-missing {
  background: #f1f5f9;
  color: #94a3b8;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
}

.trans-missing-text {
  font-size: 0.85rem;
  color: #94a3b8;
  font-style: italic;
}

.sci-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

/* Empty State */
.sci-empty-state {
  padding: 60px 24px;
  text-align: center;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 400px;
  margin: 0 auto;
}

.empty-icon-halo {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 16px;
}

.empty-title {
  margin: 0 0 6px 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
}

.empty-desc {
  margin: 0;
  font-size: 0.9rem;
  color: #64748b;
}
</style>
